/**
 * Pure parser for Azure Storage connection strings and bare SAS / public URLs
 * (spec §4.1 "Connection-string paste", §4.2 anonymous attachment). Process-
 * agnostic: the renderer paste field and the main-process `connections_add`
 * normalisation both import it. The secret is returned in a dedicated field
 * and never appears in an `invalid` reason.
 */
import type { AzureStorageEndpoints, StorageResourceRef, StorageService } from '../../types/azureStorage';
import { AZURITE_DEFAULT_ACCOUNT, AZURITE_DEFAULT_PORTS, AZURITE_WELL_KNOWN_KEY, isLoopbackHost, normalizeStorageAccountInput, readEmulatorEndpoints } from './azureStorageUrl';
import { PLAIN_SEMICOLON_SYNTAX, scanKeyValues } from '../utils/connectionStringTokens';
import { isAccountSasFacts, parseSasFacts, resourceFromUrlPath, sasQueryOf, serviceOfUrl, shareResourceFromUrl, type SasFacts } from './azureStorageSasUrl';

export type { SasFacts } from './azureStorageSasUrl';

export type AzureStorageParseResult =
  | { kind: 'account-key'; accountName: string; accountKey: string; endpoints: AzureStorageEndpoints; suffix?: string }
  | { kind: 'sas'; accountName?: string; endpoints: AzureStorageEndpoints; sas: string; sasFacts: SasFacts }
  /**
   * The local emulator: `UseDevelopmentStorage=true`, or an account-key string whose endpoints are all Azurite's
   * (`readEmulatorEndpoints`, `azureStorageUrl.ts`). `services` = Azurite's services the string gives; `unusedKeys` = keys of the
   * string an emulator connection does not use (a loopback `FileEndpoint`, `EndpointSuffix`).
   */
  | { kind: 'development-storage'; accountName: string; accountKey: string; endpoints: AzureStorageEndpoints; services: StorageService[]; unusedKeys?: string[] }
  // `pathKind` (S7-10): a share path whose kind the URL cannot tell — `resource` is the file candidate; never persisted.
  | { kind: 'sas-url'; endpoint: string; accountName?: string; resource: StorageResourceRef; sas: string; sasFacts: SasFacts; pathKind?: 'ambiguous' }
  | { kind: 'account-sas-url'; endpoint: string; accountName?: string; endpoints: AzureStorageEndpoints; sas: string; sasFacts: SasFacts }
  | { kind: 'sas-query'; sas: string; sasFacts: SasFacts }
  | { kind: 'public-url'; endpoint: string; accountName?: string; resource: StorageResourceRef; pathKind?: 'ambiguous' }
  /** Recognisably ours, but unusable; `reason` is user-facing and secret-free. */
  | { kind: 'invalid'; reason: string };

const ENDPOINT_KEYS: ReadonlyArray<[key: string, service: keyof AzureStorageEndpoints]> = [
  ['blobendpoint', 'blob'],
  ['tableendpoint', 'table'],
  ['queueendpoint', 'queue'],
  ['fileendpoint', 'file'],
];
/** The keywords of an Azure Storage connection string, lower-case (the paste's format detection reads them too). */
export const AZURE_STORAGE_KEYS: ReadonlySet<string> = new Set([
  'accountname', 'accountkey', 'sharedaccesssignature', 'usedevelopmentstorage', 'developmentstorageproxyuri',
  'defaultendpointsprotocol', 'endpointsuffix', ...ENDPOINT_KEYS.map(([key]) => key),
]);

/**
 * `null` when the input is not an Azure Storage connection string or URL at all
 * (the `ParserMatch` precedent), `{ kind: 'invalid', reason }` when it is ours
 * but malformed, else the typed parse.
 */
export function parseAzureStorageConnectionString(input: string): AzureStorageParseResult | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return parseUrl(trimmed);
  if (!trimmed.includes('=')) return null;
  if (!trimmed.includes(';')) {
    const params = new URLSearchParams(trimmed.replace(/^\?/, ''));
    if (params.has('sv') && params.has('sig')) return { kind: 'sas-query', sas: trimmed.replace(/^\?/, ''), sasFacts: parseSasFacts(params) };
  }
  return parsePairs(trimmed);
}

/**
 * The pairs are read as the Azure Storage SDK reads them — a plain `;` split, no quoting (`PLAIN_SEMICOLON_SYNTAX`,
 * Option C C3 review r3, C3-15: each format keeps its own syntax). Real storage values (base64 keys, SAS query strings,
 * endpoint URLs, names) never start with a quote or a brace, so one that does was written as if it were quoted — the SDK
 * would read the marks as part of the value and split at a `;` inside them — and the string is refused rather than
 * guessed at (C3 review r3: refuse when unsure).
 */
function parsePairs(input: string): AzureStorageParseResult | null {
  const values = new Map<string, string>();
  const originalNames = new Map<string, string>();
  let duplicate: string | undefined;
  let malformedAt: number | undefined;
  let wrapped = false;
  scanKeyValues(input, PLAIN_SEMICOLON_SYNTAX).segments.forEach((pair, index) => {
    if (!pair) {
      malformedAt ??= index + 1;
      return;
    }
    const key = pair.key.toLowerCase();
    if (values.has(key)) duplicate ??= originalNames.get(key) ?? pair.key;
    if (/^["'{]/u.test(pair.value)) wrapped = true;
    values.set(key, pair.value);
    originalNames.set(key, pair.key);
  });
  if (![...values.keys()].some((key) => AZURE_STORAGE_KEYS.has(key))) return null;
  if (wrapped) return { kind: 'invalid', reason: 'A value is in quotes or braces, which Azure Storage connection strings do not use' };
  if (malformedAt !== undefined) return { kind: 'invalid', reason: `Malformed pair #${malformedAt}: expected Key=Value` };
  if (duplicate !== undefined) return { kind: 'invalid', reason: `Duplicate key ${duplicate}` };

  const endpoints: AzureStorageEndpoints = {};
  for (const [key, service] of ENDPOINT_KEYS) {
    const value = values.get(key);
    if (value) endpoints[service] = value;
  }

  if (values.get('usedevelopmentstorage')?.toLowerCase() === 'true') {
    const base = developmentStorageBase(values.get('developmentstorageproxyuri'));
    return {
      kind: 'development-storage',
      accountName: AZURITE_DEFAULT_ACCOUNT,
      accountKey: AZURITE_WELL_KNOWN_KEY,
      endpoints: {
        blob: `${base}:${AZURITE_DEFAULT_PORTS.blob}/${AZURITE_DEFAULT_ACCOUNT}`,
        queue: `${base}:${AZURITE_DEFAULT_PORTS.queue}/${AZURITE_DEFAULT_ACCOUNT}`,
        table: `${base}:${AZURITE_DEFAULT_PORTS.table}/${AZURITE_DEFAULT_ACCOUNT}`,
      },
      services: ['blob', 'queue', 'table'],
    };
  }

  const accountName = values.get('accountname') || undefined;
  const sas = values.get('sharedaccesssignature');
  if (sas) {
    const cleaned = sas.replace(/^\?/, '');
    const firstEndpoint = Object.values(endpoints).find((value): value is string => typeof value === 'string');
    const derived = accountName ?? (firstEndpoint ? normalizeStorageAccountInput(firstEndpoint).accountName : undefined);
    if (!derived && !firstEndpoint) return { kind: 'invalid', reason: 'A SAS connection string needs AccountName or at least one *Endpoint' };
    const result: AzureStorageParseResult = { kind: 'sas', endpoints, sas: cleaned, sasFacts: parseSasFacts(new URLSearchParams(cleaned)) };
    return derived ? { ...result, accountName: derived } : result;
  }

  const accountKey = values.get('accountkey');
  if (accountKey) {
    if (!accountName) return { kind: 'invalid', reason: 'AccountName is required with AccountKey' };
    const suffix = values.get('endpointsuffix') || undefined;
    const emulator = readEmulatorEndpoints(accountName, endpoints, suffix);
    if (emulator.kind === 'invalid') return emulator;
    if (emulator.kind === 'emulator') {
      const unused = emulator.unusedKeys.length > 0 ? { unusedKeys: emulator.unusedKeys } : {};
      return { kind: 'development-storage', accountName, accountKey, endpoints: emulator.endpoints, services: emulator.services, ...unused };
    }
    const result: AzureStorageParseResult = { kind: 'account-key', accountName, accountKey, endpoints };
    return suffix ? { ...result, suffix } : result;
  }
  return { kind: 'invalid', reason: 'Missing AccountKey or SharedAccessSignature' };
}

function developmentStorageBase(proxyUri: string | undefined): string {
  if (proxyUri) {
    try {
      const url = new URL(proxyUri);
      return `${url.protocol}//${url.hostname}`;
    } catch {
      /* fall through to the default host */
    }
  }
  return 'http://127.0.0.1';
}

function parseUrl(raw: string): AzureStorageParseResult | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const segments = url.pathname.split('/').filter(Boolean).map((segment) => decodeURIComponent(segment));
  const pathStyle = isLoopbackHost(url.hostname);
  const service = serviceOfUrl(url, pathStyle);
  if (!service) return null;
  const accountName = pathStyle ? segments.shift() : url.hostname.split('.')[0];
  if (pathStyle && !accountName) return { kind: 'invalid', reason: 'Path-style URL is missing the account segment' };
  const endpoint = pathStyle ? `${url.protocol}//${url.host}/${accountName}` : `https://${url.host}`;
  const sasFacts = parseSasFacts(url.searchParams);
  if (segments.length === 0) {
    if (url.searchParams.has('sig') && isAccountSasFacts(sasFacts)) {
      const endpointKey = service === 'dfs' ? 'dfs' : service;
      return { kind: 'account-sas-url', endpoint, accountName, endpoints: { [endpointKey]: endpoint }, sas: url.search.slice(1), sasFacts };
    }
    return { kind: 'invalid', reason: 'Paste a container, blob, table, queue or share URL — the account root is not an attachment' };
  }
  const share = service === 'file' ? shareResourceFromUrl(url, segments, pathStyle) : undefined;
  const resource = share ? share.resource : resourceFromUrlPath(service, segments, sasFacts.signedResource);
  if (!resource) return { kind: 'invalid', reason: `A ${service} URL must name one resource` };
  const ambiguous = share?.pathKind ? { pathKind: share.pathKind } : {};
  const sas = sasQueryOf(url);
  if (!sas) return { kind: 'public-url', endpoint, accountName, resource, ...ambiguous };
  return { kind: 'sas-url', endpoint, accountName, resource, sas, sasFacts, ...ambiguous };
}
